import React, { useState, useEffect } from "react";
import { customFetch } from '../apiClient';

const EditRoomForm = () => {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    setLoading(true);
    setError(null);
    try {
     const res = await customFetch(`/Room`);
      if (!res.ok) throw new Error("Failed to load rooms");
      const data = await res.json();
      setRooms(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (room) => {
    setEditId(room.room_ID);
    setEditData({
      room_Category: room.room_Category || "",
      roomNo: room.roomNo || "",
      room_Charge: room.room_Charge || 0,
      remarks: room.remarks || "",
      roomSTS: room.roomSTS ?? true,
    });
    setError(null);
    setMessage(null);
  };

  const handleChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: field === "room_Charge" ? parseFloat(value) || 0 : value,
    }));
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditData({});
    setError(null);
    setMessage(null);
  };

  const saveEdit = async () => {
    setLoading(true);
    setError(null);
    setMessage(null);
    try {

      const payload = {
        room_ID: editId,
        ...editData,
        hid: Number(sessionStorage.getItem("hospitalId")),
      };
      if (typeof payload.roomSTS === "string")
        payload.roomSTS = payload.roomSTS === "true";

  const res = await customFetch(`/Room/${editId}`, {
  method: "PUT",
  headers: {
    "Content-Type": "application/json"
  },
  body: JSON.stringify(payload),
});

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to update room");
      }

      setRooms(prev => prev.map(r => r.room_ID === editId ? { ...r, ...payload } : r));
      setMessage("Room updated successfully!");
      cancelEdit();
      
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteRoom = async (id) => {
    if (!window.confirm("Are you sure you want to delete this room?")) return;
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
     const res = await customFetch(`/Room/${id}`, {
  method: "DELETE"
});
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to delete room");
      }
      setRooms(prev => prev.filter(r => r.room_ID !== id));
      setMessage("Room deleted successfully!");
      
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredRooms = rooms.filter(room =>
    room.room_Category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    room.roomNo?.toString().includes(searchQuery)
  );

  return (
    <div className="edit-room-container">
      <div className="edit-room-card">
        {/* Header */}
        <div className="room-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-door-open-fill"></i>
            </div>
            <div className="header-text">
              <h5>Rooms Management</h5>
              <p>View, edit and manage room details</p>
            </div>
          </div>
          <div className="header-stats">
            <div className="stat-item">
              <i className="bi bi-door-closed"></i>
              <span>{rooms.length} Rooms</span>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError(null)}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}
        {message && (
          <div className="alert-custom alert-success">
            <i className="bi bi-check-circle-fill"></i>
            <span>{message}</span>
            <button className="alert-close" onClick={() => setMessage(null)}>
              <i className="bi bi-x"></i>
            </button>
          </div>
        )}

        <div className="room-body">
          {/* Toolbar */}
          <div className="table-toolbar">
            <div className="search-box">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search by category or room no..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button className="btn-refresh" onClick={fetchRooms} disabled={loading}>
              <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
              Refresh
            </button>
          </div>

          {/* Table */}
          <div className="table-container">
            {loading && rooms.length === 0 ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading rooms...</p>
              </div>
            ) : filteredRooms.length === 0 ? (
              <div className="empty-state">
                <i className="bi bi-inbox"></i>
                <h6>No Rooms Found</h6>
                <p>{searchQuery ? "No matches found" : "No rooms available"}</p>
              </div>
            ) : (
              <table className="rooms-table">
                <thead>
                  <tr>
                    <th style={{ width: '50px', textAlign: 'center' }}>Sr</th>
                    <th>Category</th>
                    <th>Room No</th>
                    <th className="text-right">Charge (₹)</th>
                    <th className="text-center">Status</th>
                    <th style={{ width: '90px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRooms.map((room, index) => (
                    <tr key={room.room_ID} className={editId === room.room_ID ? 'editing' : ''}>
                      <td className="text-center">
                        <span className="sr-badge">{index + 1}</span>
                      </td>
                      
                      {editId === room.room_ID ? (
                        <>
                          <td>
                            <input
                              type="text"
                              className="form-input-edit"
                              value={editData.room_Category}
                              onChange={e => handleChange("room_Category", e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-input-edit"
                              value={editData.roomNo}
                              onChange={e => handleChange("roomNo", e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="form-input-edit text-right"
                              step="0.01"
                              min="0"
                              value={editData.room_Charge}
                              onChange={e => handleChange("room_Charge", e.target.value)}
                            />
                          </td>
                          <td>
                            <select
                              className="form-select-edit"
                              value={String(editData.roomSTS)}
                              onChange={e => handleChange("roomSTS", e.target.value)}
                            >
                              <option value="true">Active</option>
                              <option value="false">Inactive</option>
                            </select>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn-action btn-save" onClick={saveEdit} disabled={loading} title="Save">
                                <i className="bi bi-check-lg"></i>
                              </button>
                              <button className="btn-action btn-cancel" onClick={cancelEdit} title="Cancel">
                                <i className="bi bi-x-lg"></i>
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>
                            <div className="cell-flex">
                              <i className="bi bi-tag-fill cell-icon"></i>
                              <span className="fw-500">{room.room_Category}</span>
                            </div>
                          </td>
                          <td>
                            <div className="cell-flex">
                              <i className="bi bi-door-closed-fill cell-icon-alt"></i>
                              <span className="fw-500">{room.roomNo}</span>
                            </div>
                          </td>
                          <td className="text-right">
                            <span className="room-charge">₹ {Number(room.room_Charge).toFixed(2)}</span>
                          </td>
                          <td className="text-center">
                            <span className={`status-badge ${room.roomSTS ? 'active' : 'inactive'}`}>
                              {room.roomSTS ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="btn-icon btn-edit" onClick={() => startEditing(room)} title="Edit">
                                <i className="bi bi-pencil-square"></i>
                              </button>
                              <button className="btn-icon btn-delete" onClick={() => deleteRoom(room.room_ID)} title="Delete">
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <style jsx>{`
        .edit-room-container {
          padding: 0;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .edit-room-card {
          background: white;
          border-radius: 6px;
          box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }

        /* Animations */
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinning { animation: spin 1s linear infinite; }

        /* Header */
        .room-header {
          background: var(--theme-color, #0088cc);
          padding: 8px 12px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid #00d4aa;
          flex-wrap: wrap;
          gap: 10px;
        }

        .header-left {
          display: flex; align-items: center; gap: 8px;
        }

        .header-icon {
          width: 28px; height: 28px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 4px; display: flex; align-items: center; justify-content: center;
          font-size: 14px; color: white;
        }

        .header-text h5 { margin: 0; color: white; font-size: 14px; font-weight: 600; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10px; }

        .header-stats { display: flex; gap: 8px; }
        .stat-item {
          background: rgba(255, 255, 255, 0.2);
          padding: 4px 10px; border-radius: 4px; display: flex; align-items: center; gap: 6px;
          color: white; font-size: 11px; font-weight: 600;
        }

        /* Alerts */
        .alert-custom {
          margin: 8px 12px 0; padding: 6px 10px; border-radius: 4px;
          display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 500; position: relative;
        }
        .alert-danger { background: #fff5f5; color: #e53e3e; border: 1px solid #feb2b2; }
        .alert-success { background: #f0fff4; color: #27ae60; border: 1px solid #9ae6b4; }
        .alert-close { position: absolute; right: 8px; background: none; border: none; cursor: pointer; opacity: 0.6; }

        /* Body */
        .room-body { padding: 12px; }

        /* Toolbar */
        .table-toolbar {
          padding: 8px 10px; background: #f7fafc; border-radius: 4px; margin-bottom: 12px;
          display: flex; justify-content: space-between; align-items: center; gap: 10px;
          border: 1px solid #e2e8f0;
        }

        .search-box { flex: 1; max-width: 300px; position: relative; }
        .search-box i {
          position: absolute; left: 10px; top: 50%; transform: translateY(-50%);
          color: #a0aec0; font-size: 12px;
        }
        .search-box input {
          width: 100%; padding: 6px 10px 6px 28px; border: 1px solid #cbd5e0;
          border-radius: 4px; font-size: 11px; outline: none; transition: 0.2s;
        }
        .search-box input:focus { border-color: var(--theme-color, #3182ce); box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }

        .btn-refresh {
          padding: 6px 12px; border-radius: 4px; border: 1px solid #cbd5e0;
          background: white; color: #4a5568; font-size: 11px; font-weight: 600;
          cursor: pointer; transition: 0.2s; display: flex; align-items: center; gap: 6px;
        }
        .btn-refresh:hover:not(:disabled) { background: #edf2f7; }

        /* Loading / Empty States */
        .loading-state, .empty-state { text-align: center; padding: 40px 10px; }
        .spinner {
          width: 24px; height: 24px; border: 3px solid #edf2f7; border-top-color: var(--theme-color, #3182ce);
          border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 10px;
        }
        .loading-state p, .empty-state p { color: #718096; font-size: 11px; margin: 0; }
        .empty-state h6 { margin: 0 0 4px 0; font-size: 13px; font-weight: 600; color: #2d3748; }
        .empty-state i { font-size: 32px; color: #cbd5e0; margin-bottom: 8px; display: block; }

        /* Table Container (Responsive) */
        .table-container {
          overflow-x: auto; -webkit-overflow-scrolling: touch;
          border: 1px solid #e2e8f0; border-radius: 4px;
        }

        /* Table Compact */
        .rooms-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .rooms-table thead { background: #edf2f7; border-bottom: 1px solid #cbd5e0; }
        .rooms-table th {
          padding: 8px 10px; text-align: left; font-weight: 600; color: #4a5568; white-space: nowrap; text-transform: uppercase;
        }
        .rooms-table tbody tr { border-bottom: 1px solid #e2e8f0; transition: 0.2s; }
        .rooms-table tbody tr:hover { background: #f7fafc; }
        .rooms-table tbody tr.editing { background: #ebf8ff; }
        .rooms-table td { padding: 6px 10px; color: #2d3748; vertical-align: middle; }

        .text-center { text-align: center; }
        .text-right { text-align: right; }
        .fw-500 { font-weight: 500; }

        .sr-badge {
          background: var(--theme-color, #3182ce); color: white; padding: 2px 6px;
          border-radius: 4px; font-weight: 600; font-size: 10px;
        }

        .cell-flex { display: flex; align-items: center; gap: 6px; }
        .cell-icon { color: var(--theme-color, #3182ce); font-size: 12px; }
        .cell-icon-alt { color: #38a169; font-size: 12px; }
        .room-charge { font-weight: 600; color: #2f855a; }

        .status-badge {
          padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; display: inline-block;
        }
        .status-badge.active { background: #c6f6d5; color: #22543d; }
        .status-badge.inactive { background: #fed7d7; color: #742a2a; }

        /* Inline Edit Inputs */
        .form-input-edit, .form-select-edit {
          width: 100%; padding: 4px 6px; border: 1px solid var(--theme-color, #3182ce);
          border-radius: 3px; font-size: 11px; outline: none; background: white;
        }
        .form-input-edit:focus, .form-select-edit:focus { box-shadow: 0 0 0 1px var(--theme-color, #3182ce); }

        /* Action Buttons */
        .action-buttons { display: flex; gap: 4px; justify-content: center; }
        .btn-icon, .btn-action {
          width: 24px; height: 24px; border-radius: 4px; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center; font-size: 12px; transition: 0.2s;
        }
        
        .btn-edit { background: #ebf8ff; color: #3182ce; }
        .btn-edit:hover { background: #bee3f8; }
        .btn-delete { background: #fff5f5; color: #e53e3e; }
        .btn-delete:hover { background: #fed7d7; }
        
        .btn-save { background: #c6f6d5; color: #2f855a; }
        .btn-save:hover:not(:disabled) { background: #9ae6b4; }
        .btn-cancel { background: #edf2f7; color: #4a5568; }
        .btn-cancel:hover { background: #e2e8f0; }
        .btn-action:disabled { opacity: 0.5; cursor: not-allowed; }

        /* Responsive Breakpoints */
        @media (max-width: 600px) {
          .table-toolbar { flex-direction: column; align-items: stretch; }
          .search-box { max-width: 100%; }
        }
      `}</style>
    </div>
  );
};

export default EditRoomForm;